import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { RouterModule } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';

import { ClinicPlanService } from '../../../core/services/clinic-plan.service';

/** Non-owners only hear about the plan in the last days before the app locks. */
const STAFF_WARNING_DAYS = 3;

function daysUntil(isoDate: string): number {
  const end = new Date(`${isoDate.slice(0, 10)}T00:00:00`);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round((end.getTime() - today.getTime()) / 86_400_000);
}

/**
 * Strip above the app while the branch's clinic-app plan is in grace, expired
 * or cancelled. Owners see it whenever that is so (it links to Subscriptions);
 * everyone else only in the last days of grace. Dismissal lasts the session.
 */
@Component({
  selector: 'app-plan-banner',
  imports: [TranslateModule, RouterModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (message(); as m) {
      <div
        class="flex items-center justify-center gap-3 px-10 py-2 text-sm font-semibold relative"
        [class]="m.severe ? 'bg-rose-50 text-rose-800' : 'bg-amber-50 text-amber-900'"
        role="status"
        aria-live="polite"
      >
        <span>{{ m.key | translate: { count: m.days } }}</span>
        @if (isOwner()) {
          <a class="underline" routerLink="/settings" [queryParams]="{ tab: 'subscriptions' }">
            {{ 'plan_banner_view' | translate }}
          </a>
        }
        <button
          type="button"
          class="absolute right-2 top-1/2 -translate-y-1/2 rounded px-2 py-1 hover:bg-black/5 focus-visible:outline focus-visible:outline-2"
          [attr.aria-label]="'close' | translate"
          (click)="dismissed.set(true)"
        >
          ×
        </button>
      </div>
    }
  `,
})
export class PlanBanner {
  private readonly plans = inject(ClinicPlanService);

  protected readonly dismissed = signal(false);
  protected readonly isOwner = this.plans.isOwner;

  protected readonly message = computed(() => {
    const plan = this.plans.plan();
    if (this.dismissed() || !plan || !this.plans.needsWarning()) return null;
    const clinic = plan.clinic;
    if (clinic.state === 'grace') {
      const days = clinic.grace_until ? Math.max(0, daysUntil(clinic.grace_until)) : 0;
      if (!plan.is_owner && days > STAFF_WARNING_DAYS) return null;
      return { key: 'plan_banner_grace', days, severe: days <= STAFF_WARNING_DAYS };
    }
    // Expired or cancelled. Only reachable here while the app is not locked
    // (enforcement off), so it is the owner's business.
    if (!plan.is_owner) return null;
    return { key: `plan_banner_${clinic.state}`, days: 0, severe: true };
  });
}
